'use client';

import { useId, useRef, useState } from 'react';

import { uploadSecretFile } from '@eliadmin/core/src/api/general/general-api';
import { CONTACT_COPY } from '@eliadmin/core/src/components/dlz/contact/contact-copy';
import { Icon } from '@eliadmin/core/src/components/dlz/ui/icon';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';

/** Limits of the core contact form (its texts `text3457` / `text3458` name them). */
const MAX_FILE_SIZE = 2 * 1024 * 1024;
const MAX_FILES = 15;
/** The file types the design names in the hint. */
const ACCEPT = '.jpg,.jpeg,.png,.pdf';

/**
 * The core attachment logic of the contact form: a selection over the limits is rejected as a whole,
 * the files are uploaded one by one as secret files on submit and sent as `attachments` ids.
 */
export function useContactAttachments() {
  const { t } = useTranslations();
  const locale = useLocaleStore(state => state.currentLocale);
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);

  const select = (selected: File[]) => {
    setError(null);
    setFiles([]);
    if (selected.length > MAX_FILES) setError(`${t('text3457').trim()} ${MAX_FILES}`);
    else if (selected.some(file => file.size > MAX_FILE_SIZE)) setError(t('text3458'));
    else setFiles(selected);
  };

  /** Ids of the uploaded files, or null (and the error shown) when an upload fails. */
  const upload = async () => {
    const ids: { id: number }[] = [];
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      try {
        const response = await uploadSecretFile({ formData, locale });
        if (response.data?.id) ids.push({ id: response.data.id });
      } catch {
        setError(t('text3459'));
        return null;
      }
    }
    return ids;
  };

  return { files, error, setError, select, upload };
}

type ContactAttachmentsProps = Pick<
  ReturnType<typeof useContactAttachments>,
  'files' | 'error' | 'select'
> & { disabled?: boolean };

/**
 * Figma „Attachment“ (60338:23906, phone 60438:80708): dashed border/default box with the icon, the
 * texts and „Vybrať súbory“ in a row in a wide card, centred and stacked in a narrow one.
 */
export function ContactAttachments({ files, error, select, disabled }: ContactAttachmentsProps) {
  const { t } = useTranslations();
  const inputRef = useRef<HTMLInputElement>(null);
  const id = useId();

  return (
    <div className="flex flex-col items-center gap-4 rounded-[18px] border border-dashed border-[#D5D8DC] p-4 text-center @[28rem]:flex-row @[28rem]:px-5 @[28rem]:text-left">
      <Icon name="download" size={32} className="text-[#D7191F]" />
      <div className="flex w-full min-w-0 flex-col gap-1 @[28rem]:flex-1">
        <p id={`${id}-title`} className="dlz-body-s-medium text-[#0F1012]">
          {CONTACT_COPY.attachments.title}
        </p>
        <p id={`${id}-hint`} className="dlz-caption text-[#6B7079]">
          {CONTACT_COPY.attachments.hint(MAX_FILE_SIZE / 1024 / 1024)}
        </p>
        {files.length ? (
          <p className="dlz-caption text-[#0F1012] [overflow-wrap:anywhere]">
            {files.map(file => file.name).join(', ')}
          </p>
        ) : null}
        {error ? (
          <p role="alert" className="dlz-caption text-[#D7191F]">
            {error}
          </p>
        ) : null}
      </div>
      <Button
        type="button"
        variant="secondary"
        size="m"
        disabled={disabled}
        aria-describedby={`${id}-title ${id}-hint`}
        onClick={() => inputRef.current?.click()}
        className="w-full @[28rem]:w-auto"
      >
        {t('text3456')}
      </Button>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPT}
        tabIndex={-1}
        aria-label={t('text3456')}
        className="hidden"
        onChange={event => select(Array.from(event.target.files ?? []))}
      />
    </div>
  );
}
